import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
import { useState, type KeyboardEvent, type ReactElement } from 'react';

import { Conversations } from '@ant-design/x';
import { Button, Checkbox, Popconfirm, Space, Spin, message } from 'antd';

import type { ChatConversationItem } from '../api/chat.client.js';

export interface ConversationSwitcherProps {
  conversations: ChatConversationItem[];
  value: string | null;
  isLoading: boolean;
  disabled?: boolean;
  /** Кто смотрит: удалять можно только собственные диалоги. */
  currentUserId?: string | null;
  onChange: (conversationId: string) => void;
  onCreate?: () => void;
  /** Удаление выбранных; без обработчика управление удалением не показывается. */
  onDelete?: (conversationIds: string[]) => Promise<void>;
  isDeleting?: boolean;
}

function getConversationLabel(conversation: ChatConversationItem): string {
  const title =
    conversation.title?.trim() ||
    i18n.t('crm.features.dynamic.features.ai.chat.components.conversationswitcher.15', {
      p0: conversation.id,
    });
  return conversation.visibility === 'shared'
    ? i18n.t('crm.features.dynamic.features.ai.chat.components.conversationswitcher.16', {
        p0: title,
      })
    : title;
}

export function ConversationSwitcher({
  conversations,
  value,
  isLoading,
  disabled = false,
  currentUserId = null,
  onChange,
  onCreate,
  onDelete,
  isDeleting = false,
}: ConversationSwitcherProps): ReactElement {
  useI18nSubscription();
  // Режим выбора: вместо переключения диалогов — отметка на удаление.
  // Отдельный режим, а не чекбоксы поверх обычного списка: клик по строке
  // не должен одновременно и переключать разговор, и менять отметку.
  const [selecting, setSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  const selectValue = value ?? '';
  const hasSelectedConversation = conversations.some(
    (conversation) => conversation.id === selectValue,
  );
  const items = conversations.map((conversation) => {
    const isActive = conversation.id === selectValue;

    return {
      key: conversation.id,
      label: getConversationLabel(conversation),
      disabled,
      role: 'option',
      tabIndex: disabled ? -1 : 0,
      'aria-selected': isActive,
      onKeyDown: (event: KeyboardEvent<HTMLLIElement>) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onChange(conversation.id);
        }
      },
    };
  });

  const ownConversations = conversations.filter(
    (conversation) => currentUserId !== null && conversation.userId === currentUserId,
  );
  const canManage = onDelete !== undefined && ownConversations.length > 0;

  const stopSelecting = (): void => {
    setSelecting(false);
    setSelectedIds(new Set());
  };

  const toggle = (conversationId: string): void => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  };

  return (
    <div
      className="rounded-xl border border-stone-200 bg-white p-3"
      data-testid="conversation-switcher"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
          {i18n.t('crm.features.features.ai.chat.components.conversationswitcher.dialog')}
        </p>
        {canManage && !selecting ? (
          <Button
            type="link"
            size="small"
            disabled={disabled}
            style={{ fontSize: 11, padding: 0, height: 'auto' }}
            onClick={() => setSelecting(true)}
          >
            {i18n.t('crm.features.features.ai.chat.components.conversationswitcher.vybrat')}
          </Button>
        ) : null}
        {selecting ? (
          <Space size={8}>
            <Popconfirm
              title={i18n.t(
                'crm.features.dynamic.features.ai.chat.components.conversationswitcher.17',
                { p0: selectedIds.size },
              )}
              description={i18n.t(
                'crm.features.features.ai.chat.components.conversationswitcher.perepiska_udalitsya_bezvozvratno',
              )}
              okText={i18n.t(
                'crm.features.features.ai.chat.components.conversationswitcher.udalit',
              )}
              cancelText={i18n.t(
                'crm.features.features.ai.chat.components.conversationswitcher.otmena',
              )}
              okButtonProps={{ danger: true, loading: isDeleting }}
              onConfirm={async () => {
                // Отказ сервера показывается тостом, а выбор остаётся: молча
                // закрывшийся режим выглядел бы как успешное удаление.
                try {
                  await onDelete?.([...selectedIds]);
                  stopSelecting();
                } catch {
                  void message.error(
                    i18n.t(
                      'crm.features.features.ai.chat.components.conversationswitcher.ne_udalos_udalit_dialogi',
                    ),
                  );
                }
              }}
            >
              <Button
                danger
                size="small"
                style={{ fontSize: 11 }}
                disabled={selectedIds.size === 0 || isDeleting || disabled}
                loading={isDeleting}
              >
                {i18n.t(
                  'crm.features.dynamic.features.ai.chat.components.conversationswitcher.18',
                  { p0: selectedIds.size },
                )}
              </Button>
            </Popconfirm>
            <Button
              size="small"
              style={{ fontSize: 11 }}
              disabled={isDeleting}
              onClick={stopSelecting}
            >
              {i18n.t('crm.features.features.ai.chat.components.conversationswitcher.otmena_2')}
            </Button>
          </Space>
        ) : null}
      </div>
      {isLoading ? (
        <div className="flex min-h-20 items-center justify-center">
          <Spin size="small" />
        </div>
      ) : selecting ? (
        <ul
          className="max-h-48 space-y-1 overflow-y-auto"
          aria-label={i18n.t(
            'crm.features.features.ai.chat.components.conversationswitcher.vybor_dialogov_na_udalenie',
          )}
        >
          {conversations.map((conversation) => {
            const own = currentUserId !== null && conversation.userId === currentUserId;
            return (
              <li key={conversation.id} className="flex items-center gap-2 text-xs">
                <Checkbox
                  checked={selectedIds.has(conversation.id)}
                  // Чужой диалог виден по доступу, но распоряжаться им нельзя.
                  disabled={!own || isDeleting}
                  onChange={() => toggle(conversation.id)}
                >
                  <span className="text-xs">{getConversationLabel(conversation)}</span>
                </Checkbox>
              </li>
            );
          })}
        </ul>
      ) : (
        <Conversations
          items={items}
          role="listbox"
          aria-label={i18n.t(
            'crm.features.features.ai.chat.components.conversationswitcher.dialog_2',
          )}
          {...(hasSelectedConversation ? { activeKey: selectValue } : {})}
          {...(onCreate
            ? {
                creation: {
                  label: i18n.t(
                    'crm.features.features.ai.chat.components.conversationswitcher.novyy_dialog',
                  ),
                  icon: () => null,
                  disabled,
                  onClick: onCreate,
                },
              }
            : {})}
          className="max-h-48 overflow-y-auto"
          classNames={{ item: 'text-xs' }}
          onActiveChange={(conversationId) => {
            if (!disabled) {
              onChange(conversationId);
            }
          }}
        />
      )}
    </div>
  );
}
